/**
 * AddressLookupSelector — wraps the legacy AddressLookup or the new
 * AddressLookupV2 depending on the rollback flag.
 *
 * Phase 2 (2026-05-08): v2 is the default. The legacy component renders only
 * when the user has explicitly opted into legacy via `?address-picker=legacy`
 * or `localStorage.address_picker_v2 === "0"`.
 *
 * Public API mirrors the legacy AddressLookupProps verbatim, so any consumer
 * site can swap a single import without changing call-site props.
 */
import React, { useEffect, useState } from 'react';
import LegacyAddressLookup from '../AddressLookup';
import type { AddressLookupProps } from '../AddressLookup';
import AddressLookupV2 from './AddressLookupV2';
import { shouldRenderLegacyAddressLookup } from './flag';

export type AddressLookupSelectorProps = AddressLookupProps;

export default function AddressLookupSelector(
  props: AddressLookupSelectorProps,
) {
  // Read the flag once on mount. We deliberately do NOT live-listen for
  // localStorage changes — toggling mid-flow would unmount + remount the
  // form and reset state, which is a worse UX than picking a side at mount.
  const [renderLegacy] = useState<boolean>(() =>
    shouldRenderLegacyAddressLookup(),
  );

  // Surface the active side at debug time only — never user-visible.
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.location.hostname.startsWith('test')
    ) {
      // eslint-disable-next-line no-console
      console.debug(
        `[AddressLookupSelector] active variant: ${renderLegacy ? 'legacy' : 'v2'}`,
      );
    }
  }, [renderLegacy]);

  if (renderLegacy) {
    return <LegacyAddressLookup {...props} />;
  }
  return <AddressLookupV2 {...props} />;
}
